import { Link } from "react-router-dom";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Instagram, Mail } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useEmbed } from "@/contexts/EmbedContext";
import { useFooterDestinations } from "@/hooks/use-footer-destinations";
import { LanguageSwitcher } from "@/components/public/LanguageSwitcher";
import { useUiTranslations } from "@/hooks/use-ui-translations";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import logoMain from "@/assets/guestly-logo-main.png";

/* ------------------------------------------------------------------ */
/*  Config                                                            */
/* ------------------------------------------------------------------ */

interface FooterLink {
  labelKey: string;
  href: string;
  external?: boolean;
  enabled: boolean;
}

interface FooterSection {
  id: string;
  labelKey: string;
  links: FooterLink[];
}

const FOOTER_SECTIONS: FooterSection[] = [
  {
    id: "explore",
    labelKey: "footer.explore",
    links: [
      { labelKey: "footer.allExperiences", href: "/experiences", enabled: true },
      { labelKey: "footer.destinationsDirectory", href: "/destinations", enabled: true },
      { labelKey: "footer.giftCards", href: "/gifts", enabled: false },
      { labelKey: "footer.blog", href: "/blog", enabled: false },
    ],
  },
  {
    id: "support",
    labelKey: "footer.support",
    links: [
      { labelKey: "footer.helpFaq", href: "/help", enabled: false },
      { labelKey: "footer.contact", href: "mailto:experiences@guestlyhomes.com", external: true, enabled: true },
      { labelKey: "footer.cancellation", href: "/cancellation", enabled: false },
    ],
  },
  {
    id: "company",
    labelKey: "footer.company",
    links: [
      { labelKey: "footer.about", href: "/about", enabled: true },
      { labelKey: "footer.forHosts", href: "/partner", enabled: true },
      { labelKey: "footer.affiliateProgram", href: "/affiliates", enabled: true },
      { labelKey: "footer.careers", href: "/careers", enabled: false },
      { labelKey: "footer.press", href: "/press", enabled: false },
    ],
  },
  {
    id: "trustLegal",
    labelKey: "footer.trustLegal",
    links: [
      { labelKey: "footer.terms", href: "/terms", enabled: true },
      { labelKey: "footer.privacy", href: "/privacy", enabled: true },
      { labelKey: "footer.sitemap", href: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sitemap`, external: true, enabled: true },
      { labelKey: "footer.cookieSettings", href: "/cookies", enabled: false },
      { labelKey: "footer.accessibility", href: "/accessibility", enabled: false },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Analytics helpers                                                 */
/* ------------------------------------------------------------------ */

function fireFooterClick(section: string, text: string, href: string) {
  window.dispatchEvent(
    new CustomEvent("footer_link_click", { detail: { section, text, href } })
  );
}

function fireCityClick(city: string, slug: string, href: string) {
  window.dispatchEvent(
    new CustomEvent("footer_city_click", { detail: { city, slug, href } })
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

function FooterLinkItem({
  link,
  sectionId,
  t,
}: {
  link: FooterLink;
  sectionId: string;
  t: (key: string) => string;
}) {
  const label = t(link.labelKey);
  const handleClick = () => fireFooterClick(sectionId, label, link.href);

  if (link.external) {
    return (
      <li>
        <a
          href={link.href}
          target={link.href.startsWith("mailto:") ? undefined : "_blank"}
          rel={link.href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
          onClick={handleClick}
          className="text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          {label}
        </a>
      </li>
    );
  }

  return (
    <li>
      <LocalizedLink
        to={link.href}
        onClick={handleClick}
        className="text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        {label}
      </LocalizedLink>
    </li>
  );
}

function SectionLinks({ section, t }: { section: FooterSection; t: (key: string) => string }) {
  const enabled = section.links.filter((l) => l.enabled);
  if (enabled.length === 0) return null;

  return (
    <ul className="space-y-2.5">
      {enabled.map((link) => (
        <FooterLinkItem key={link.labelKey} link={link} sectionId={section.id} t={t} />
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

export function SiteFooter() {
  const { isEmbed } = useEmbed();
  const { data: destinations, isLoading: destinationsLoading, isError: destinationsError } = useFooterDestinations();
  const { t } = useUiTranslations();

  const hasDestinations = destinations && destinations.length > 0;

  if (isEmbed) return null;

  const visibleSections = FOOTER_SECTIONS.filter(
    (s) => s.links.some((l) => l.enabled)
  );

  return (
    <footer className="border-t border-border bg-card mt-auto">
      <div className="mx-auto max-w-6xl px-4 py-12 md:py-16">
        {/* ── Desktop layout ── */}
        <div className="hidden md:block">
          <div className="grid grid-cols-6 gap-8">
            {/* Brand column */}
            <div className="col-span-2 pr-4">
              <LocalizedLink to="/" className="inline-block mb-4">
                <img src={logoMain} alt="Guestly Experiences" className="h-7 w-auto opacity-80" />
              </LocalizedLink>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("footer.tagline")}
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                {t("footer.taglineSub")}
              </p>

              {/* Social icons */}
              <div className="flex items-center gap-3 mt-5">
                <a
                  href="https://www.instagram.com/guestlyexperiences"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Guestly on Instagram"
                  onClick={() => fireFooterClick("brand", "Instagram", "https://www.instagram.com/guestlyexperiences")}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a
                  href="mailto:experiences@guestly.com"
                  aria-label="Email Guestly"
                  onClick={() => fireFooterClick("brand", "Email", "mailto:experiences@guestly.com")}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Mail className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Destinations column */}
            <nav aria-label={t("footer.destinations")}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground mb-4">
                {t("footer.destinations")}
              </h3>
              {destinationsLoading && (
                <div className="space-y-2.5">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-4 w-28" />
                  ))}
                </div>
              )}
              {destinationsError && (
                <ul className="space-y-2.5">
                  <li>
                    <LocalizedLink to="/destinations" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                      {t("footer.viewAllDestinations")}
                    </LocalizedLink>
                  </li>
                </ul>
              )}
              {hasDestinations && (
                <ul className="space-y-2.5">
                  {destinations.map((d) => {
                    const href = `/destinations/${d.countrySlug}/${d.slug}`;
                    const anchorText = t("destination.experiencesIn").replace("{city}", d.name);
                    return (
                      <li key={d.slug}>
                        <LocalizedLink
                          to={href}
                          onClick={() => {
                            fireFooterClick("destinations", anchorText, href);
                            fireCityClick(d.name, d.slug, href);
                          }}
                          className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                        >
                          {anchorText}
                        </LocalizedLink>
                      </li>
                    );
                  })}
                  <li>
                    <LocalizedLink
                      to="/destinations"
                      onClick={() => fireFooterClick("destinations", t("footer.viewAllDestinations"), "/destinations")}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground font-medium"
                    >
                      {t("footer.viewAllDestinations")} →
                    </LocalizedLink>
                  </li>
                </ul>
              )}
            </nav>

            {/* Config-driven columns */}
            {visibleSections.map((section) => (
              <nav key={section.id} aria-label={t(section.labelKey)}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground mb-4">
                  {t(section.labelKey)}
                </h3>
                <SectionLinks section={section} t={t} />
              </nav>
            ))}
          </div>
        </div>

        {/* ── Mobile layout (accordion) ── */}
        <div className="md:hidden">
          {/* Brand block — always visible */}
          <div className="mb-6">
            <LocalizedLink to="/" className="inline-block mb-3">
              <img src={logoMain} alt="Guestly Experiences" className="h-6 w-auto opacity-80" />
            </LocalizedLink>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("footer.tagline")}
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              {t("footer.taglineSub")}
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a
                href="https://www.instagram.com/guestlyexperiences"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Guestly on Instagram"
                onClick={() => fireFooterClick("brand", "Instagram", "https://www.instagram.com/guestlyexperiences")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="mailto:experiences@guestly.com"
                aria-label="Email Guestly"
                onClick={() => fireFooterClick("brand", "Email", "mailto:experiences@guestly.com")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Mail className="h-4 w-4" />
              </a>
            </div>
          </div>

          <Accordion type="multiple" className="w-full">
            {/* Destinations accordion */}
            <AccordionItem value="destinations">
              <AccordionTrigger className="text-xs font-semibold uppercase tracking-wider text-foreground hover:no-underline">
                {t("footer.destinations")}
              </AccordionTrigger>
              <AccordionContent>
                {destinationsLoading && (
                  <div className="space-y-2.5">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-4 w-28" />
                    ))}
                  </div>
                )}
                {hasDestinations && (
                  <ul className="space-y-2.5">
                    {destinations.map((d) => {
                      const href = `/destinations/${d.countrySlug}/${d.slug}`;
                      const anchorText = t("destination.experiencesIn").replace("{city}", d.name);
                      return (
                        <li key={d.slug}>
                          <LocalizedLink
                            to={href}
                            onClick={() => {
                              fireFooterClick("destinations", anchorText, href);
                              fireCityClick(d.name, d.slug, href);
                            }}
                            className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                          >
                            {anchorText}
                          </LocalizedLink>
                        </li>
                      );
                    })}
                    <li>
                      <LocalizedLink
                        to="/destinations"
                        className="text-sm text-muted-foreground transition-colors hover:text-foreground font-medium"
                      >
                        {t("footer.viewAllDestinations")} →
                      </LocalizedLink>
                    </li>
                  </ul>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Config-driven accordion sections */}
            {visibleSections.map((section) => (
              <AccordionItem key={section.id} value={section.id}>
                <AccordionTrigger className="text-xs font-semibold uppercase tracking-wider text-foreground hover:no-underline">
                  {t(section.labelKey)}
                </AccordionTrigger>
                <AccordionContent>
                  <SectionLinks section={section} t={t} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* ── Bottom bar ── */}
        <div className="mt-12 pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} Guestly Homes Sweden AB · Org. nr: 559359-3238 · 944 91 Hortlax
            </p>
            <p className="text-xs text-muted-foreground/60">
              {t("footer.trademark")}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <p className="text-xs text-muted-foreground/60">
              {t("footer.createdIn")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
